(() => {
  let lastTrigger = null;
  let reservationTimer = null;

  const drawer = () => document.querySelector('[data-cart-drawer][data-enabled="true"]');

  const storageGet = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  };

  const storageSet = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (_error) {
      // The cart keeps working when browser storage is unavailable.
    }
  };

  const storageRemove = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (_error) {
      // Nothing to clean up when browser storage is unavailable.
    }
  };

  const responseError = async (response, fallback) => {
    try {
      const payload = await response.json();
      return payload.description || payload.message || fallback;
    } catch (_error) {
      return fallback;
    }
  };

  const setHeaderCount = (count) => {
    document.querySelectorAll('[data-header-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count < 1;
    });
  };

  const setDrawerState = (element, loading, message = '') => {
    if (!element) return;
    element.classList.toggle('is-loading', loading);
    const status = element.querySelector('[data-cart-drawer-status]');
    if (status) status.textContent = message;
  };

  const closeDrawer = () => {
    const element = drawer();
    if (!element) return;
    element.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('oslo-cart-drawer-open');
    if (lastTrigger?.focus) lastTrigger.focus();
  };

  const openDrawer = (trigger = null) => {
    const element = drawer();
    if (!element) return;
    lastTrigger = trigger || lastTrigger;
    element.setAttribute('aria-hidden', 'false');
    document.body.classList.add('oslo-cart-drawer-open');
    window.requestAnimationFrame(() => element.querySelector('[data-cart-drawer-close]:not(.oslo-cart-drawer__overlay)')?.focus());
    startReservationTimer(element);
  };

  const fetchCart = async (element = drawer()) => {
    const response = await fetch(element.dataset.cartJsonUrl, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  };

  const refreshDrawer = async ({ open = true } = {}) => {
    const current = drawer();
    if (!current) return null;
    const wasOpen = current.getAttribute('aria-hidden') === 'false';
    const response = await fetch(`${current.dataset.cartUrl}?section_id=${encodeURIComponent(current.dataset.sectionId)}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Cart section request failed');
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('[data-cart-drawer]');
    if (!fresh) throw new Error('Cart drawer section missing');
    current.replaceWith(fresh);
    startReservationTimer(fresh);
    if (open || wasOpen) openDrawer(lastTrigger);
    return fresh;
  };

  const updateCart = async (key, quantity) => {
    const element = drawer();
    if (!element) return;
    setDrawerState(element, true, element.dataset.loadingLabel);
    try {
      const response = await fetch(element.dataset.cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error('Cart change failed');
      const cart = await response.json();
      setHeaderCount(cart.item_count);
      await refreshDrawer({ open: true });
    } catch (error) {
      setDrawerState(drawer(), false, element.dataset.errorText);
    }
  };

  const setCouponStatus = (element, message, state = '') => {
    const status = element?.querySelector('[data-cart-coupon-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-success', state === 'success');
    status.classList.toggle('is-error', state === 'error');
  };

  const applyCoupon = async (form) => {
    const element = drawer();
    if (!element) return;

    const input = form.querySelector('input[name="discount"]');
    const button = form.querySelector('[data-cart-coupon-button]');
    const code = input?.value.trim() || '';

    if (!code) {
      setCouponStatus(element, element.dataset.couponEmpty, 'error');
      input?.focus();
      return;
    }

    const defaultLabel = button?.dataset.defaultLabel || button?.textContent || '';
    if (button) {
      button.disabled = true;
      button.textContent = button.dataset.loadingLabel || defaultLabel;
    }
    setCouponStatus(element, '');

    try {
      const response = await fetch(element.dataset.cartUpdateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ discount: code })
      });
      if (!response.ok) throw new Error(await responseError(response, element.dataset.couponInvalid));

      const cart = await response.json();
      const normalisedCode = code.toLowerCase();
      const discountCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
      const codeResult = discountCodes.find((entry) => String(entry.code || '').toLowerCase() === normalisedCode);
      const applications = [
        ...(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
        ...(Array.isArray(cart.items) ? cart.items.flatMap((item) => (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application || {})) : [])
      ];
      const applicationFound = applications.some((application) => String(application.title || application.code || '').toLowerCase() === normalisedCode);
      const rejected = codeResult?.applicable === false || (discountCodes.length > 0 && !codeResult && !applicationFound);

      const fresh = await refreshDrawer({ open: true });
      const freshInput = fresh?.querySelector('input[name="discount"]');
      if (freshInput) freshInput.value = code;
      setCouponStatus(fresh, rejected ? fresh.dataset.couponInvalid : fresh.dataset.couponSuccess, rejected ? 'error' : 'success');
    } catch (error) {
      const activeDrawer = drawer() || element;
      setCouponStatus(activeDrawer, error.message || activeDrawer.dataset.couponInvalid, 'error');
    } finally {
      const activeButton = drawer()?.querySelector('[data-cart-coupon-button]') || button;
      if (activeButton) {
        activeButton.disabled = false;
        activeButton.textContent = activeButton.dataset.defaultLabel || defaultLabel;
      }
    }
  };

  const startReservationTimer = (element = drawer()) => {
    if (reservationTimer) window.clearInterval(reservationTimer);
    if (!element?.querySelector('[data-cart-reservation-timer]')) return;

    const minutes = Number(element.dataset.reservationMinutes || 10);
    const storageKey = 'osloCartReservationEndsAt';
    let end = Number(storageGet(storageKey));
    if (!end || end <= Date.now()) {
      end = Date.now() + minutes * 60 * 1000;
      storageSet(storageKey, String(end));
    }

    const tick = () => {
      const output = drawer()?.querySelector('[data-cart-reservation-timer]');
      if (!output) return;
      let remaining = Math.max(0, end - Date.now());
      if (remaining === 0) {
        end = Date.now() + minutes * 60 * 1000;
        storageSet(storageKey, String(end));
        remaining = minutes * 60 * 1000;
      }
      const totalSeconds = Math.floor(remaining / 1000);
      const mins = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
      const seconds = String(totalSeconds % 60).padStart(2, '0');
      output.textContent = `${mins}:${seconds}`;
    };

    tick();
    reservationTimer = window.setInterval(tick, 1000);
  };

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-cart-drawer-open]');
    if (openButton && drawer()) {
      event.preventDefault();
      openDrawer(openButton);
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      closeDrawer();
      return;
    }

    const item = event.target.closest('[data-cart-item]');
    if (!item) return;
    const key = item.dataset.lineKey;
    const quantity = Number(item.querySelector('.oslo-cart-drawer__quantity span')?.textContent || 1);

    if (event.target.closest('[data-cart-quantity-plus]')) updateCart(key, quantity + 1);
    if (event.target.closest('[data-cart-quantity-minus]')) updateCart(key, Math.max(0, quantity - 1));
    if (event.target.closest('[data-cart-remove]')) updateCart(key, 0);
  });

  document.addEventListener('keydown', (event) => {
    const element = drawer();
    if (!element || element.getAttribute('aria-hidden') === 'true') return;
    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...element.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
      .filter((node) => node.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('submit', async (event) => {
    const couponForm = event.target.closest('[data-cart-coupon-form]');
    if (couponForm) {
      event.preventDefault();
      await applyCoupon(couponForm);
      return;
    }

    const form = event.target.closest('.os-product-form');
    const submitter = event.submitter;
    const element = drawer();
    if (!form || !element || (submitter && !submitter.matches('[data-add-to-cart]'))) return;

    event.preventDefault();
    const button = form.querySelector('[data-add-to-cart]');
    const originalLabel = button?.textContent || '';
    lastTrigger = button;
    openDrawer(button);
    setDrawerState(element, true, element.dataset.loadingLabel);

    if (button) {
      button.disabled = true;
      button.textContent = element.dataset.loadingLabel;
    }

    try {
      const formData = new FormData(form);
      if (!formData.has('quantity')) formData.set('quantity', '1');
      const response = await fetch(element.dataset.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      if (!response.ok) {
        throw new Error(await responseError(response, element.dataset.errorText));
      }
      const cart = await fetchCart(element);
      setHeaderCount(cart.item_count);

      try {
        await refreshDrawer({ open: true });
      } catch (_refreshError) {
        storageSet('oslo-cart-drawer-open', 'true');
        window.location.reload();
        return;
      }
    } catch (error) {
      const activeDrawer = drawer() || element;
      setDrawerState(activeDrawer, false, error.message || activeDrawer.dataset.errorText);
      openDrawer(button);
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    startReservationTimer(drawer());

    if (storageGet('oslo-cart-drawer-open') === 'true') {
      storageRemove('oslo-cart-drawer-open');
      openDrawer();
    }
  });
})();
